import { useState } from "react";
import { generateAdminInsights } from "@/projects/lib/ai.functions";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import { toast } from "sonner";

type Props = {
  range: string;
  total: number;
  pending: number;
  assigned: number;
  reward: number;
  notProper: number;
  teachers: number;
  rewardRate: number;
  verificationRate: number;
  topTeachers: { name: string; verified: number }[];
};

function renderMd(md: string) {
  // Ultra-light markdown: **bold**, ## / ### headings, and bullet lines.
  const lines = md.split("\n");
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;
    if (trimmed.startsWith("### ")) return <div key={i} className="mt-2 text-sm font-semibold text-royal">{trimmed.slice(4)}</div>;
    if (trimmed.startsWith("## ")) return <div key={i} className="mt-3 text-sm font-bold text-royal">{trimmed.slice(3)}</div>;
    if (trimmed.startsWith("# ")) return <div key={i} className="mt-3 text-base font-bold text-royal">{trimmed.slice(2)}</div>;
    const bullet = /^[-*]\s+/.test(trimmed);
    const content = bullet ? trimmed.replace(/^[-*]\s+/, "") : trimmed;
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**")
        ? <strong key={j} className="text-foreground">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );
    return bullet ? (
      <div key={i} className="flex gap-2 text-sm text-muted-foreground"><span className="text-gold">◆</span><span>{parts}</span></div>
    ) : (
      <div key={i} className="text-sm text-muted-foreground">{parts}</div>
    );
  });
}

export function AiInsightsPanel(props: Props) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      // Build a plain, serializable payload — never pass `props` directly
      // because dev-mode JSX tagging may inject a non-serializable `ref`.
      const payload = {
        range: props.range,
        total: props.total,
        pending: props.pending,
        assigned: props.assigned,
        reward: props.reward,
        notProper: props.notProper,
        teachers: props.teachers,
        rewardRate: props.rewardRate,
        verificationRate: props.verificationRate,
        topTeachers: props.topTeachers.map((t) => ({ name: t.name, verified: t.verified })),
      };
      const res = await generateAdminInsights(payload);
      setText(res.text);
    } catch (e) {
      toast.error("Couldn't generate AI insights right now.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl border-2 border-gold/40 bg-gradient-to-br from-royal-soft via-card to-gold-soft/40 p-5 shadow-card">
      <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-gold/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-royal/20 blur-3xl" />

      <div className="relative flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-gold to-saffron text-white shadow-soft">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-base font-bold text-royal">AI Executive Insights</div>
              <span className="rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-royal">New</span>
            </div>
            <div className="text-xs text-muted-foreground">Auto-generated summary, risks &amp; recommendations from your current data.</div>
          </div>
        </div>
        <Button onClick={run} disabled={loading} className="bg-royal hover:bg-royal/90">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing…</> : text ? <><RefreshCw className="mr-2 h-4 w-4" /> Regenerate</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate insights</>}
        </Button>
      </div>

      <div className="relative mt-4">
        {!text && !loading && (
          <div className="rounded-lg border border-dashed border-royal/30 bg-background/60 p-4 text-sm text-muted-foreground">
            Click <strong className="text-royal">Generate insights</strong> to let AI summarize submission trends, spot bottlenecks, and suggest next actions.
          </div>
        )}
        {loading && (
          <div className="space-y-2">
            <div className="h-3 w-2/3 animate-pulse rounded bg-royal/10" />
            <div className="h-3 w-11/12 animate-pulse rounded bg-royal/10" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-royal/10" />
            <div className="h-3 w-9/12 animate-pulse rounded bg-royal/10" />
          </div>
        )}
        {text && (
          <div className="space-y-1 rounded-lg border bg-background/70 p-4 backdrop-blur">
            {renderMd(text)}
          </div>
        )}
      </div>
    </div>
  );
}
